import type { RefObject } from 'react';
import { useCallback } from 'react';

import type { ApiJobId, ApiVolopId } from '#/api/types/ApiTypedId';
import type { ScrollAreaHandle } from '#/components/ScrollArea/ScrollArea';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';

type Args = {
  scrollingParentRef: RefObject<ScrollAreaHandle | null>;
};

export function useViewListingPageSidebarResultsScroll({
  scrollingParentRef,
}: Args) {
  const scrollToListingId = useCallback(
    (listingId: ApiJobId | ApiVolopId) => {
      const scrollingParent = scrollingParentRef.current?.viewportElement;
      if (!scrollingParent) return;

      const hitElement = scrollingParent.querySelector<HTMLElement>(
        `#${getSearchHitElementId(listingId)}`,
      );

      if (!hitElement) return;

      const isFirstChild =
        hitElement.parentNode?.firstElementChild === hitElement;

      /* When the user selects the first search hit, we want to show the "save search" button
       * This number doesn't have to be exact, it just needs to be big enough to reach the top of the scrolling container
       */
      const scrollMarginTop = isFirstChild ? 200 : 8;

      // Don't use hitElement.scrollIntoView() - it can cause the whole body to scroll
      scrollingParent.scrollTo(0, hitElement.offsetTop - scrollMarginTop);
    },
    [scrollingParentRef],
  );

  return { scrollToListingId };
}
